import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface RepairIntakeLengthEntry {
  componentLabel: string;
  diameterOrNPS: string;
  defectLength: string;
}

export interface RepairIntakeComponentEntry {
  nominalPipeSize: string;
  outsideDiameter: string;
  schedule: string;
  nominalWallThickness: string;
}

export interface RepairIntakeDesiredLengthEntry {
  componentLabel: string;
  diameterOrNPS: string;
  desiredRepairLength: string;
}

export interface RepairIntakeFormData {
  section1: {
    componentTypes: string[];
    componentTypeOther?: string;
    materialsGrades: string;
    componentEntries: RepairIntakeComponentEntry[];
  };
  section2: {
    utReadingsAttached: "Yes" | "No" | "";
    utThickness: string;
    deepestCorrosionDepth: string;
  };
  section3: {
    repairTypes: string[];
    repairTypeOther?: string;
    designPressure: string;
    designTemperature: string;
    operatingPressure: string;
    operatingTemperature: string;
  };
  section4: {
    serviceFluid: string;
    sdsAttached: "Yes" | "No" | "";
  };
  section5: {
    defectTypes: string[];
    defectTypeOther?: string;
    defectLengthEntries: RepairIntakeLengthEntry[];
    holeOrSlotSize: string;
    desiredRepairLengthEntries: RepairIntakeDesiredLengthEntry[];
  };
  section6: {
    activeLeak: "Yes" | "No" | "";
    leakMitigationPlan: string;
    clientLeakStopPlan: string;
  };
  section7: {
    installedUnderPressure: "Yes" | "No" | "";
    desiredRepairLife: string;
    desiredRepairLifeOther?: string;
    goodPipeOnLandingZones: "Yes" | "No" | "";
    inchesOfGoodPipe: string;
    defectsFieldVerified: "Yes" | "No" | "";
    additionalNotes: string;
  };
  section8: {
    yourName: string;
    customerName: string;
  };
  section9: {
    attachments: File[];
  };
}

type SectionNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

type RepairIntakeStore = {
  formData: RepairIntakeFormData;
  submitted: boolean;
  setSection: <TSection extends SectionNumber>(
    sectionNumber: TSection,
    data: RepairIntakeFormData[`section${TSection}`]
  ) => void;
  resetForm: () => void;
  submitForm: () => void;
};

const createInitialState = (): RepairIntakeFormData => ({
  section1: {
    componentTypes: [],
    componentTypeOther: "",
    materialsGrades: "",
    componentEntries: [
      {
        nominalPipeSize: "",
        outsideDiameter: "",
        schedule: "",
        nominalWallThickness: "",
      },
    ],
  },
  section2: {
    utReadingsAttached: "",
    utThickness: "",
    deepestCorrosionDepth: "",
  },
  section3: {
    repairTypes: [],
    repairTypeOther: "",
    designPressure: "",
    designTemperature: "",
    operatingPressure: "",
    operatingTemperature: "",
  },
  section4: {
    serviceFluid: "",
    sdsAttached: "",
  },
  section5: {
    defectTypes: [],
    defectTypeOther: "",
    defectLengthEntries: [{ componentLabel: "Component 1", diameterOrNPS: "", defectLength: "" }],
    holeOrSlotSize: "",
    desiredRepairLengthEntries: [{ componentLabel: "Component 1", diameterOrNPS: "", desiredRepairLength: "" }],
  },
  section6: {
    activeLeak: "",
    leakMitigationPlan: "",
    clientLeakStopPlan: "",
  },
  section7: {
    installedUnderPressure: "",
    desiredRepairLife: "",
    desiredRepairLifeOther: "",
    goodPipeOnLandingZones: "",
    inchesOfGoodPipe: "",
    defectsFieldVerified: "",
    additionalNotes: "",
  },
  section8: {
    yourName: "",
    customerName: "",
  },
  section9: {
    attachments: [],
  },
});

const mergeWithInitialState = (persistedState: unknown, currentState: RepairIntakeStore): RepairIntakeStore => {
  const initialState = createInitialState();
  const state = (persistedState ?? {}) as Partial<{ formData: Partial<RepairIntakeFormData>; submitted: boolean }>;
  const persistedFormData = state.formData ?? {};

  return {
    ...currentState,
    submitted: state.submitted ?? false,
    formData: {
      ...initialState,
      ...persistedFormData,
      section1: {
        ...initialState.section1,
        ...persistedFormData.section1,
        componentEntries:
          persistedFormData.section1?.componentEntries && persistedFormData.section1.componentEntries.length > 0
            ? persistedFormData.section1.componentEntries
            : initialState.section1.componentEntries,
      },
      section5: {
        ...initialState.section5,
        ...persistedFormData.section5,
        defectLengthEntries:
          persistedFormData.section5?.defectLengthEntries && persistedFormData.section5.defectLengthEntries.length > 0
            ? persistedFormData.section5.defectLengthEntries
            : initialState.section5.defectLengthEntries,
        desiredRepairLengthEntries:
          persistedFormData.section5?.desiredRepairLengthEntries &&
          persistedFormData.section5.desiredRepairLengthEntries.length > 0
            ? persistedFormData.section5.desiredRepairLengthEntries
            : initialState.section5.desiredRepairLengthEntries,
      },
      section9: {
        attachments: [],
      },
    },
  };
};

const useRepairIntakeStore = create<RepairIntakeStore>()(
  persist(
    (set) => ({
      formData: createInitialState(),
      submitted: false,
      setSection: (sectionNumber, data) =>
        set((state) => ({
          submitted: false,
          formData: {
            ...state.formData,
            [`section${sectionNumber}`]: data,
          },
        })),
      resetForm: () =>
        set({
          formData: createInitialState(),
          submitted: false,
        }),
      submitForm: () => set({ submitted: true }),
    }),
    {
      name: "repair-intake-form",
      storage: createJSONStorage(() => localStorage),
      merge: (persistedState, currentState) => mergeWithInitialState(persistedState, currentState),
      // Files stay in the live Zustand state, but localStorage should only
      // persist the serializable draft fields.
      partialize: (state) => ({
        submitted: state.submitted,
        formData: {
          ...state.formData,
          section9: {
            attachments: [],
          },
        },
      }),
    }
  )
);

export default useRepairIntakeStore;
